import z from 'zod';

export const AnalyticsEventCategory = z.enum([
  'opportunity',
  'operation',
  'ask_toro',
  'activity',
  'insight',
  'bullhorn',
  'chrome_extension',
]);
export type AnalyticsEventCategory = z.infer<typeof AnalyticsEventCategory>;

export const AnalyticsAction = z.enum([
  'submit',
  'view',
  'create',
  'update',
  'delete',
  'start',
  'end',
  'copy',
  'scroll',
  'search',
  'change',
  'select',
  'click',
  'dismiss',
  'export',
  'open',
  'inject',
]);
export type AnalyticsAction = z.infer<typeof AnalyticsAction>;

export const NotificationAction = z.enum([
  AnalyticsAction.enum.click,
  AnalyticsAction.enum.dismiss,
]);
export type NotificationAction = z.infer<typeof NotificationAction>;

const GeneralEventObject = z.enum([
  'button',
  'list',
  'card',
  'modal',
  'tab',
  'link',
  'form',
  'dropdown',
  'menu',
  'icon',
]);
export type GeneralEventObject = z.infer<typeof GeneralEventObject>;

export const ActivityEventObject = z.enum([
  ...GeneralEventObject.options,
  'image',
  'event_icon',
  'day_row',
  'justification_quote',
]);
export type ActivityEventObject = z.infer<typeof ActivityEventObject>;

export const AskToroEventObject = z.enum([
  ...GeneralEventObject.options,
  'prompt',
  'message',
  'blockquote',
  'feedback_modal',
  'opportunity_prompt',
]);
export type AskToroEventObject = z.infer<typeof AskToroEventObject>;

export const OpportunityEventObject = z.enum([
  ...GeneralEventObject.options,
  'filter',
  'sort',
  'owner',
  'action',
  'saved_search',
  'detail',
]);
export type OpportunityEventObject = z.infer<typeof OpportunityEventObject>;

const BullhornEventObject = z.enum([...GeneralEventObject.options]);
export type BullhornEventObject = z.infer<typeof BullhornEventObject>;

const OperationEventObject = z.enum([
  ...GeneralEventObject.options,
  'filter',
  'detail',
  'batch',
  're-run',
]);
export type OperationEventObject = z.infer<typeof OperationEventObject>;

const InsightEventObject = z.enum([...GeneralEventObject.options, 'feedback_modal']);
export type InsightEventObject = z.infer<typeof InsightEventObject>;

const ChromeExtensionEventObject = z.enum([
  ...GeneralEventObject.options,
  'settings_button',
  'notification',
]);
export type ChromeExtensionEventObject = z.infer<typeof ChromeExtensionEventObject>;

export const AnalyticsEventObject = z.union([
  AskToroEventObject,
  ActivityEventObject,
  OpportunityEventObject,
  BullhornEventObject,
  OperationEventObject,
  InsightEventObject,
  ChromeExtensionEventObject,
]);
export type AnalyticsEventObject = z.infer<typeof AnalyticsEventObject>;

export type CategoryToObject = {
  opportunity: OpportunityEventObject;
  operation: OperationEventObject;
  ask_toro: AskToroEventObject;
  activity: ActivityEventObject;
  bullhorn: BullhornEventObject;
  insight: InsightEventObject;
  settings: GeneralEventObject;
  chrome_extension: ChromeExtensionEventObject;
};

export const Product = z.enum(['salesIq', 'talentIq', 'staffIq', 'dataIq', 'chrome_extension']);
export type Product = z.infer<typeof Product>;

export interface EventParams<T extends AnalyticsEventCategory> {
  category: T;
  object?: CategoryToObject[T];
  action: AnalyticsAction;
  properties?: Record<string, unknown>;
}

export const AnalyticsEventInput = z.object({
  category: AnalyticsEventCategory,
  action: AnalyticsAction,
  object: AnalyticsEventObject.optional(),
  properties: z.record(z.string(), z.unknown()).optional(),
});
export type AnalyticsEventInput = z.infer<typeof AnalyticsEventInput>;

/**
 * Event naming convention: `{category}:{object}_{action}`
 * Example: "opportunity:filter_click", "ask_toro:message_submit"
 */
export function buildAnalyticsEventName<T extends AnalyticsEventCategory>(params: EventParams<T>) {
  const { category, object, action } = params;
  const categoryString = category ? `${category}:` : '';
  const objectString = object ? `${object}_` : '';
  return `${categoryString}${objectString}${action}`;
}
